import React, { useState } from 'react';
import './QuickConnectModal.css';

/**
 * QuickConnectModal — replaces the old window.confirm for connecting from posts.
 * Shows pre-written messages so users can connect with one tap.
 * This is the key UX that differentiates Belong from Reddit:
 * "I relate to this" → real connection → progressive disclosure → real relationship.
 */

const QUICK_MESSAGES = [
  { emoji: '💜', text: "This really resonates with me. I'd love to connect with someone who gets it." },
  { emoji: '🤝', text: "I share this experience too. Would be great to have someone to talk to about it." },
  { emoji: '🙏', text: "Thank you for sharing this. I feel less alone reading it. Let's connect." },
  { emoji: '💪', text: "I'm going through something similar. Would love to support each other." },
  { emoji: '✨', text: "Your words really spoke to me. I'd love to connect and share experiences." },
];

function QuickConnectModal({ isOpen, onClose, onConnect, postPreview, channelName }) {
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [sending, setSending] = useState(false);

  if (!isOpen) return null;

  const handleSend = async () => {
    const message = useCustom ? customMessage.trim() : (selectedMessage !== null ? QUICK_MESSAGES[selectedMessage].text : null);
    if (!message || message.length < 20) return;
    setSending(true);
    try {
      await onConnect(message);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setSending(false);
    }
  };

  const currentMessage = useCustom ? customMessage : (selectedMessage !== null ? QUICK_MESSAGES[selectedMessage].text : '');
  const isValid = currentMessage.length >= 20 && currentMessage.length <= 300;

  return (
    <div className="qc-overlay" onClick={onClose}>
      <div className="qc-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="qc-header">
          <h3>🤝 Connect with this person</h3>
          <button className="qc-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Post preview */}
        {postPreview && (
          <div className="qc-post-preview">
            <span className="qc-post-icon">👤</span>
            <p>"{postPreview.length > 120 ? postPreview.substring(0, 120) + '...' : postPreview}"</p>
          </div>
        )}

        {/* Value prop */}
        <div className="qc-value-prop">
          <p>On Belong, you can go from anonymous post to real conversation. Start anonymous, then gradually share more — only when both sides agree.</p>
        </div>

        {/* Quick messages */}
        <div className="qc-section-label">Tap to send:</div>
        <div className="qc-quick-messages">
          {QUICK_MESSAGES.map((msg, i) => (
            <button
              key={i}
              className={`qc-quick-msg ${selectedMessage === i && !useCustom ? 'selected' : ''}`}
              onClick={() => { setSelectedMessage(i); setUseCustom(false); }}
            >
              <span className="qc-quick-emoji">{msg.emoji}</span>
              <span className="qc-quick-text">{msg.text}</span>
            </button>
          ))}
        </div>

        {/* Custom message toggle */}
        <button
          className={`qc-custom-toggle ${useCustom ? 'active' : ''}`}
          onClick={() => setUseCustom(!useCustom)}
        >
          ✏️ Write your own message
        </button>

        {useCustom && (
          <div className="qc-custom-input">
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Tell them why you want to connect (20-300 chars)..."
              maxLength={300}
              rows={3}
            />
            <span className="qc-char-count">{customMessage.length}/300</span>
          </div>
        )}

        {/* Privacy note */}
        <div className="qc-privacy-note">
          🔒 You stay anonymous until both sides agree to reveal more. Your identity is never shared without your consent.
        </div>

        {/* Send button */}
        <button
          className="qc-send-btn"
          onClick={handleSend}
          disabled={!isValid || sending}
        >
          {sending ? 'Sending...' : 'Send Connection Request →'}
        </button>
      </div>
    </div>
  );
}

export default QuickConnectModal;
